import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { MessageCircle, Send, X } from 'lucide-react'
import { useSettings } from '@/context/SettingsContext'
import { messengerUrl } from '@/utils/messenger'
import { whatsAppUrl } from '@/utils/whatsapp'
import { Logo } from '@/components/ui/Logo'
import { cn } from '@/utils/cn'
import { WhatsAppIcon } from '@/components/ui/WhatsAppIcon'

/**
 * In-page chat widget — the "open on the website not in new tab" experience.
 *
 * How it works:
 *   1. A floating bubble sits at bottom-right.
 *   2. Click it → an inline chat panel slides up ON the page.
 *   3. Customer picks a channel — WhatsApp (default) or Messenger — and types
 *      a message in our UI.
 *   4. "Send" opens wa.me with the text pre-filled (WhatsApp carries it
 *      through), or m.me for Messenger (which does not, so the shopper
 *      re-types there — the message lands in the Facebook Business Suite inbox).
 *
 * Why the handoff: neither Meta product offers a free way to deliver a message
 * from a website straight into an inbox — Meta retired the Customer Chat
 * Plugin, and the WhatsApp Business API is paid. This widget gives the on-page
 * chat UX everyone expects while still routing every message to the right
 * place.
 *
 * The WhatsApp number is the store's `whatsappNumber` setting (digits with
 * country code, which is exactly the wa.me format), so the owner changes it at
 * /admin/settings like everything else.
 *
 * This is support only. Ordering happens on-site at /checkout.
 */

const starterMessages = (brandName) => [
  `Hi! 👋 Welcome to ${brandName}.`,
  "How can we help you today? Tell us what you're looking for and we'll reply within minutes.",
]

const QUICK_REPLIES = [
  'Is this product in stock?',
  'Do you deliver to my area?',
  'Can I get a discount on bulk orders?',
]

const CHANNELS = {
  whatsapp: {
    label: 'WhatsApp',
    placeholder: 'Message us on WhatsApp…',
    note: 'Your message will open in WhatsApp, ready to send.',
  },
  messenger: {
    label: 'Messenger',
    placeholder: 'Message us on Messenger…',
    note: 'Your message will be delivered to our Facebook Business Suite inbox.',
  },
}


export function ChatWidget() {
  const { brandName, messengerPageId, whatsappNumber } = useSettings()
  const [open, setOpen] = useState(false)
  const [channel, setChannel] = useState('whatsapp')
  const [message, setMessage] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // Lock body scroll on mobile while the panel is open so the chat feels
  // like a real popup rather than just an overlay.
  useEffect(() => {
    if (!open) return undefined
    const original = document.body.style.overflow
    if (window.matchMedia('(max-width: 640px)').matches) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  const send = (text) => {
    const body = (text ?? message).trim()
    if (!body) return
    // Both deep links must open in a new window — browsers refuse to navigate
    // the current document to them on mobile. On mobile this hands off to the
    // installed app; on desktop it opens WhatsApp Web / Messenger on the web.
    const url =
      channel === 'whatsapp'
        ? whatsAppUrl(body, whatsappNumber)
        : messengerUrl(messengerPageId)
    window.open(url, '_blank', 'noopener,noreferrer')
    setMessage('')
  }

  return (
    <>
      {/* Floating bubble */}
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Open chat'}
        animate={{ scale: open ? 0.9 : 1 }}
        className="chat-bubble fixed bottom-5 right-4 z-40 grid h-14 w-14 place-items-center rounded-full bg-coral text-white shadow-modal transition hover:scale-105 sm:right-6 lg:bottom-6"
      >
        {!open && (
          <span className="absolute inset-0 -z-10 animate-pulse-ring rounded-full bg-coral/40" />
        )}
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="msg" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <MessageCircle size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.aside
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            role="dialog"
            aria-label="Chat with us"
            className="chat-panel fixed bottom-24 right-4 z-40 flex w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-charcoal-100 bg-white shadow-modal sm:right-6"
          >
            {/* Header */}
            <header className="flex items-center gap-3 bg-coral px-4 py-3 text-white">
              <Logo size={36} className="rounded-full bg-white p-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold">{brandName}</div>
                <div className="flex items-center gap-1.5 text-xs text-white/90">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-300" />
                  Typically replies in minutes
                </div>
              </div>
              <button
                type="button"
                aria-label="Close chat"
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 hover:bg-white/15"
              >
                <X size={18} />
              </button>
            </header>

            {/* Channel switcher */}
            <div
              role="radiogroup"
              aria-label="Chat channel"
              className="flex gap-1 border-b border-charcoal-100 bg-white p-2"
            >
              {Object.entries(CHANNELS).map(([key, c]) => {
                const active = channel === key
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setChannel(key)}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition',
                      active
                        ? key === 'whatsapp'
                          ? 'bg-[#25D366] text-white'
                          : 'bg-[#0084FF] text-white'
                        : 'text-charcoal-500 hover:bg-sand-50'
                    )}
                  >
                    {key === 'whatsapp' ? <WhatsAppIcon /> : <MessageCircle size={14} />}
                    {c.label}
                  </button>
                )
              })}
            </div>

            {/* Conversation */}
            <div className="max-h-[38vh] min-h-[180px] overflow-y-auto overscroll-contain bg-sand-50 px-4 py-4">
              {starterMessages(brandName).map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="mb-2 max-w-[85%] rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 text-sm text-charcoal shadow-sm"
                >
                  {m}
                </motion.div>
              ))}

              <div className="mt-4 space-y-1.5">
                <div className="text-xs font-medium uppercase tracking-wide text-charcoal-400">
                  Quick questions
                </div>
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    className="block w-full rounded-full border border-coral-200 bg-white px-3 py-1.5 text-left text-xs text-coral hover:bg-coral-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => {
                e.preventDefault()
                send()
              }}
              className="flex items-end gap-2 border-t border-charcoal-100 bg-white p-3"
            >
              <textarea
                ref={inputRef}
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    send()
                  }
                }}
                placeholder={CHANNELS[channel].placeholder}
                className="max-h-32 flex-1 resize-none rounded-2xl border border-charcoal-200 px-3 py-2 text-sm outline-none focus:border-coral"
              />
              <button
                type="submit"
                disabled={!message.trim()}
                aria-label="Send message"
                className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-coral text-white transition hover:bg-coral-500 disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </form>

            <p className="border-t border-charcoal-100 bg-sand-50 px-4 py-2 text-center text-[10px] text-charcoal-400">
              {CHANNELS[channel].note}
            </p>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  )
}
